import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
    PlusCircle,
    MapPin,
    Store,
    CreditCard,
    Coins,
    Camera,
    X,
    Loader2,
    Search,
    ArrowLeft,
    Info,
    CheckCircle2,
    AlertCircle
} from "lucide-react";
import { CategoryIcon } from "@/components/CategoryIcon";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { ImageCropperModal } from "@/components/ImageCropperModal";

import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/authContext";
import { CATEGORIES, PROVINCES } from "@shared/schema";
import { findProvincesForCity, CityInfo } from "@shared/cities";
import { getAuthHeader } from "@/lib/authHelpers";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";

interface ImagePreview {
    file: File;
    preview: string;
}

export default function AddShopPage() {
    const [, setLocation] = useLocation();
    const { isLoggedIn } = useAuth();
    const { toast } = useToast();
    const { t } = useI18n();

    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [googleLink, setGoogleLink] = useState("");

    // Form State
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [province, setProvince] = useState("");
    const [lat, setLat] = useState<number | null>(null);
    const [lng, setLng] = useState<number | null>(null);
    const [googlePlaceId, setGooglePlaceId] = useState("");
    const [openingHours, setOpeningHours] = useState("");
    const [structuredOpeningHours, setStructuredOpeningHours] = useState<any[]>(
        Array.from({ length: 7 }, (_, i) => ({
            weekday: i,
            open_time: "10:00",
            close_time: "17:00",
            is_closed: i === 6 // Zondag standaard gesloten
        }))
    );

    const [hasCharity, setHasCharity] = useState(false);
    const [hasHoreca, setHasHoreca] = useState(false);
    const [hasCardPayment, setHasCardPayment] = useState(false);
    const [hasCashPayment, setHasCashPayment] = useState(false);

    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [images, setImages] = useState<string[]>([]); // Array of base64 strings
    const [facadeImage, setFacadeImage] = useState<string | null>(null);

    const [isManual, setIsManual] = useState(false);
    const [pendingImages, setPendingImages] = useState<string[]>([]);
    const [croppingIndex, setCroppingIndex] = useState(-1);
    const [croppingTarget, setCroppingTarget] = useState<"facade" | "gallery" | null>(null);
    const [provinceCandidates, setProvinceCandidates] = useState<CityInfo[]>([]);

    // Nearby Search State
    const [nearbyShops, setNearbyShops] = useState<any[]>([]);
    const [showNearbyModal, setShowNearbyModal] = useState(false);
    const [nearbyLoading, setNearbyLoading] = useState(false);
    const [existingShopOverlay, setExistingShopOverlay] = useState<{
        open: boolean;
        shopUrl: string;
        shopName: string;
    }>({
        open: false,
        shopUrl: "",
        shopName: "",
    });

    const openExistingShopOverlay = (shop: { id?: string; slug?: string; province?: string; city?: string; name?: string }) => {
        const fallbackPath = shop.province && shop.city && shop.name
            ? `/kringloopwinkels/${shop.province}/${shop.city}/${shop.name}`
            : `/kringloopwinkels/detail/${shop.id || ""}`;
        const shopUrl = shop.slug ? `/kringloopwinkels/${shop.slug}` : fallbackPath;
        setExistingShopOverlay({
            open: true,
            shopUrl,
            shopName: shop.name || "",
        });
    };

    // Authentication Check
    if (!isLoggedIn) {
        return (
            <div className="min-h-screen flex flex-col bg-slate-50">

                <main className="flex-1 container max-w-3xl mx-auto px-4 py-8 flex flex-col items-center justify-center text-center">
                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 my-12">
                        <PlusCircle className="h-16 w-16 text-primary/40 mx-auto mb-6" />
                        <h1 className="text-2xl font-bold mb-4">{t("account.loginRequired")}</h1>
                        <p className="text-slate-600 mb-8 max-w-md mx-auto">
                            {t("addShop.loginRequiredDesc")}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link href="/auth">
                                <Button size="lg" className="px-8 bg-primary hover:bg-primary/90 w-full sm:w-auto">
                                    {t("nav.login")} / {t("nav.signup")}
                                </Button>
                            </Link>
                            <Button variant="ghost" onClick={() => setLocation("/kringloopwinkels")} className="w-full sm:w-auto">
                                {t("scanner.back")}
                            </Button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Auto-detect Province when City is entered
    useEffect(() => {
        if (city && (isManual || name)) {
            const matches = findProvincesForCity(city);
            if (matches.length === 1) {
                // Only auto-set if it's different to avoid loops or overriding manual selection
                if (province !== matches[0].province) {
                    setProvince(matches[0].province);
                }
                setProvinceCandidates([]);
            } else if (matches.length > 1) {
                setProvinceCandidates(matches);
                // If the current province is NOT in the matches, clear it to force selection
                if (province && !matches.some(m => m.province === province)) {
                    setProvince("");
                }
            } else {
                setProvinceCandidates([]);
            }
        }
    }, [city, isManual, name]);

    // Auto-fill from Place Selection
    const handlePlaceSelect = async (placeId: string) => {
        setShowNearbyModal(false);
        setGoogleLoading(true);
        try {
            const authHeader = await getAuthHeader();
            const res = await fetch("/api/shops/google-details", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(authHeader || {}) },
                body: JSON.stringify({ placeId })
            });

            if (res.status === 409) {
                const data = await res.json();
                openExistingShopOverlay(data.shop || {});
                return;
            }

            if (!res.ok) throw new Error(t("addShop.fetchDetailsFailed"));

            const details = await res.json();
            setName(details.name || "");
            setAddress(details.address || "");
            setCity(details.city || "");
            setPostalCode(details.postalCode || "");
            setProvince(details.province || "");
            setLat(details.lat || null);
            setLng(details.lng || null);
            setGooglePlaceId(details.placeId || "");
            if (details.openingHoursText) setOpeningHours(details.openingHoursText);
            if (details.opening_hours) setStructuredOpeningHours(details.opening_hours);

            toast({ title: t("addShop.detailsFilledTitle"), description: t("addShop.detailsFilledDesc") });
            setIsManual(false);
        } catch (e: any) {
            toast({ title: t("common.error"), description: e.message, variant: "destructive" });
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleCurrentLocationSearch = () => {
        setNearbyLoading(true);
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(async (position) => {
                try {
                    const authHeader = await getAuthHeader();
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    const res = await fetch("/api/shops/search-nearby", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", ...(authHeader || {}) },
                        body: JSON.stringify({ lat, lng })
                    });

                    if (!res.ok) throw new Error(t("addShop.fetchNearbyFailed"));

                    const data = await res.json();
                    if (data.results && data.results.length > 0) {
                        setNearbyShops(data.results);
                        setShowNearbyModal(true);
                    } else {
                        toast({ title: t("addShop.noNearbyTitle"), description: t("addShop.noNearbyDesc") });
                    }
                } catch (e: any) {
                    toast({ title: t("common.error"), description: e.message, variant: "destructive" });
                } finally {
                    setNearbyLoading(false);
                }
            }, (error) => {
                console.error("Geolocation error:", error);
                toast({ title: t("addShop.locationErrorTitle"), description: t("addShop.locationErrorDesc"), variant: "destructive" });
                setNearbyLoading(false);
            });
        } else {
            toast({ title: t("addShop.notSupportedTitle"), description: t("addShop.notSupportedDesc"), variant: "destructive" });
            setNearbyLoading(false);
        }
    };

    // Auto-fill from Google Maps Link
    const handleGoogleLinkSearch = async () => {
        if (!googleLink.trim()) return;

        try {
            setGoogleLoading(true);
            const authHeader = await getAuthHeader();

            // We'll call a backend endpoint that can resolve Google Maps URLs/Place IDs
            const res = await fetch("/api/shops/google-details", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(authHeader || {}) },
                body: JSON.stringify({ url: googleLink })
            });

            if (res.status === 409) {
                const data = await res.json();
                openExistingShopOverlay(data.shop || {});
                return; // Stop processing
            }

            if (!res.ok) {
                throw new Error(t("addShop.linkNotResolved"));
            }

            const details = await res.json();

            setName(details.name || "");
            setAddress(details.address || "");
            setCity(details.city || "");
            setPostalCode(details.postalCode || "");
            setProvince(details.province || "");
            setLat(details.lat || null);
            setLng(details.lng || null);
            setGooglePlaceId(details.placeId || "");
            if (details.openingHoursText) setOpeningHours(details.openingHoursText);
            if (details.opening_hours) setStructuredOpeningHours(details.opening_hours);

            toast({
                title: t("addShop.shopFoundTitle"),
                description: t("addShop.shopFoundDesc"),
            });

            setIsManual(false);
        } catch (error: any) {
            toast({
                title: t("addShop.searchErrorTitle"),
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setGoogleLoading(false);
        }
    };

    const removeImage = (index: number) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const toggleCategory = (cat: string) => {
        setSelectedCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };


    const handleSubmit = async () => {
        if (!name || !address || !city) {
            toast({ title: t("addShop.incompleteTitle"), description: t("addShop.incompleteDesc"), variant: "destructive" });
            return;
        }

        try {
            setLoading(true);

            const authHeader = await getAuthHeader();

            // 1. Upload images if any
            let imageUrls: string[] = [];
            let facadeImageUrl: string | null = null;

            if (facadeImage) {
                const facadeUploadRes = await fetch("/api/upload", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(authHeader || {})
                    },
                    body: JSON.stringify({
                        images: [facadeImage],
                        bucket: "shop-photos"
                    })
                });

                if (!facadeUploadRes.ok) {
                    const errPayload = await facadeUploadRes.json().catch(() => ({} as any));
                    throw new Error(errPayload.error || t("addShop.facadeUploadFailed"));
                }
                const { urls } = await facadeUploadRes.json();
                facadeImageUrl = urls?.[0] || null;
            }

            if (images.length > 0) {
                const uploadRes = await fetch("/api/upload", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(authHeader || {})
                    },
                    body: JSON.stringify({
                        images: images,
                        bucket: "shop-photos"
                    })
                });

                if (!uploadRes.ok) {
                    const errPayload = await uploadRes.json().catch(() => ({} as any));
                    throw new Error(errPayload.error || t("addShop.photoUploadFailed"));
                }
                const { urls } = await uploadRes.json();
                imageUrls = urls;
            }

            const orderedImageUrls = facadeImageUrl
                ? [facadeImageUrl, ...imageUrls.filter((url) => url !== facadeImageUrl)]
                : imageUrls;

            // 2. Submit shop
            const shopData = {
                name,
                address,
                city,
                postalCode,
                province,
                lat,
                lng,
                googlePlaceId: googlePlaceId || undefined,
                openingHours: structuredOpeningHours.length > 0 ? structuredOpeningHours : openingHours,
                has_charity: hasCharity,
                has_horeca: hasHoreca,
                has_card_payment: hasCardPayment,
                has_cash_payment: hasCashPayment,
                categories: selectedCategories,
                imageUrls: orderedImageUrls,
                facadeImageUrl
            };

            const res = await fetch("/api/shops", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(authHeader || {})
                },
                body: JSON.stringify(shopData)
            });

            if (!res.ok) {
                const err = await res.json();

                if (err.code === "SHOP_EXISTS" && (err.slug || (err.province && err.name))) {
                    openExistingShopOverlay(err);
                    setLoading(false);
                    return;
                }

                throw new Error(err.error || t("addShop.saveShopFailed"));
            }

            toast({
                title: t("addShop.thanksTitle"),
                description: googlePlaceId
                    ? t("addShop.directAddedDesc")
                    : t("addShop.submittedReviewDesc"),
            });

            setLocation("/kringloopwinkels");
        } catch (error: any) {
            toast({
                title: t("addShop.submitErrorTitle"),
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-slate-50">


            <main className="flex-1 container max-w-3xl mx-auto px-4 py-8">
                <Button
                    variant="ghost"
                    onClick={() => setLocation("/kringloopwinkels")}
                    className="mb-4 gap-2 text-muted-foreground"
                >
                    <ArrowLeft className="h-4 w-4" />
                    {t("scanner.back")}
                </Button>

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">{t("shops.addShop")}</h1>
                    <p className="text-slate-600">
                        {t("addShop.intro")}
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Step 1: Location & Search */}
                    {!(isManual || name) && (
                        <Card className="border-primary/20 shadow-sm overflow-hidden">
                            <div className="bg-primary/5 px-6 py-3 border-b border-primary/10 flex items-center gap-2">
                                <Search className="h-5 w-5 text-primary" />
                                <span className="font-semibold text-primary">{t("addShop.searchTitle")}</span>
                            </div>
                            <CardContent className="pt-6 space-y-6">
                                {showNearbyModal ? (
                                    <div className="space-y-4">
                                        <div className="text-center pb-2">
                                            <h3 className="font-semibold text-lg">{t("addShop.foundShops")}</h3>
                                            <p className="text-sm text-muted-foreground">{t("addShop.selectShopPrompt")}</p>
                                        </div>
                                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                                            {nearbyShops.map((shop) => (
                                                <button
                                                    key={shop.place_id}
                                                    onClick={() => handlePlaceSelect(shop.place_id)}
                                                    className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 hover:border-primary/50 transition-colors flex flex-col gap-1"
                                                >
                                                    <span className="font-medium text-slate-900">{shop.name}</span>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <MapPin className="h-3 w-3" />
                                                        {shop.vicinity}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={() => setShowNearbyModal(false)}
                                        >
                                            {t("shops.cancel")}
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Option A: Current Location */}
                                        <Button
                                            onClick={handleCurrentLocationSearch}
                                            disabled={nearbyLoading || googleLoading}
                                            className="w-full h-12 text-lg bg-magenta hover:bg-magenta/90 text-white shadow-sm"
                                        >
                                            {nearbyLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <MapPin className="h-5 w-5 mr-2" />}
                                            {t("addShop.useCurrentLocation")}
                                        </Button>

                                        <div className="relative">
                                            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                                            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">{t("addShop.orViaGoogle")}</span></div>
                                        </div>

                                        {/* Option B: Google Link */}
                                        <div className="space-y-2">
                                            <Label htmlFor="google-link">{t("addShop.googleMapsLink")}</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    id="google-link"
                                                    placeholder="https://maps.app.goo.gl/..."
                                                    value={googleLink}
                                                    onChange={(e) => setGoogleLink(e.target.value)}
                                                    className="flex-1"
                                                />
                                                <Button
                                                    onClick={handleGoogleLinkSearch}
                                                    disabled={googleLoading || !googleLink.trim()}
                                                    className="bg-primary hover:bg-primary/90"
                                                >
                                                    {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                                                    {t("shops.search.button")}
                                                </Button>
                                            </div>
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Info className="h-3.5 w-3.5" />
                                                {t("addShop.pasteLinkHint")}
                                            </p>
                                        </div>

                                        {/* Option C: Manual */}
                                        <div className="text-center pt-2">
                                            <p className="text-sm border-t pt-4 text-muted-foreground mb-4">{t("addShop.cantFindPrompt")}</p>
                                            <Button
                                                variant="ghost"
                                                onClick={() => setIsManual(true)}
                                                className="text-primary font-medium"
                                            >
                                                {t("addShop.manualEntry")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 2: Main Details */}
                    {(isManual || name) && (
                        <Card className="shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-lg">{t("addShop.detailsTitle")}</CardTitle>
                                <CardDescription>{t("addShop.detailsDesc")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">{t("addShop.nameLabel")}</Label>
                                    <Input
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder={t("addShop.namePlaceholder")}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="address">{t("addShop.addressLabel")}</Label>
                                    <Input
                                        id="address"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder={t("addShop.addressPlaceholder")}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">{t("addShop.cityLabel")}</Label>
                                        <Input
                                            id="city"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="postalCode">{t("addShop.postalCodeLabel")}</Label>
                                        <Input
                                            id="postalCode"
                                            value={postalCode}
                                            onChange={(e) => setPostalCode(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="province">{t("addShop.provinceLabel")}</Label>
                                    {provinceCandidates.length > 1 && (
                                        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800 space-y-2 mb-2">
                                            <div className="flex items-center gap-2 font-medium text-amber-900">
                                                <AlertCircle className="h-4 w-4" />
                                                {t("addShop.multiplePlaces")}
                                            </div>
                                            <p>{t("addShop.multiplePlacesPrompt", { city })}</p>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                {provinceCandidates.map(c => (
                                                    <Button
                                                        key={`${c.province}-${c.municipality || ''}`}
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            setProvince(c.province);
                                                            setProvinceCandidates([]);
                                                        }}
                                                        className="bg-white border-amber-200 hover:bg-amber-100 text-amber-900"
                                                    >
                                                        {c.province} {c.municipality ? `(${c.municipality})` : ""}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <Select value={province} onValueChange={setProvince}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t("shops.province.placeholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PROVINCES.map(p => (
                                                <SelectItem key={p} value={p}>{p}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 2.5: Opening Hours */}
                    {(isManual || name) && (
                        <Card className="shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-lg">{t("addShop.openingHoursTitle")}</CardTitle>
                                <CardDescription>{t("addShop.openingHoursDesc")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {[t("shops.filters.day.monday"), t("shops.filters.day.tuesday"), t("shops.filters.day.wednesday"), t("shops.filters.day.thursday"), t("shops.filters.day.friday"), t("shops.filters.day.saturday"), t("shops.filters.day.sunday")].map((day, index) => {
                                    const hours = structuredOpeningHours.find(h => h.weekday === index) || {
                                        weekday: index,
                                        open_time: "10:00",
                                        close_time: "17:00",
                                        is_closed: false
                                    };

                                    return (
                                        <div key={day} className="flex items-center justify-between gap-4 py-2 border-b last:border-0 border-slate-100">
                                            <div className="flex items-center gap-3">
                                                <Switch
                                                    checked={!hours.is_closed}
                                                    onCheckedChange={(checked) => {
                                                        const newHours = [...structuredOpeningHours];
                                                        const existingIndex = newHours.findIndex(h => h.weekday === index);
                                                        const updated = { ...hours, is_closed: !checked };
                                                        if (existingIndex >= 0) {
                                                            newHours[existingIndex] = updated;
                                                        } else {
                                                            newHours.push(updated);
                                                        }
                                                        setStructuredOpeningHours(newHours);
                                                    }}
                                                />
                                                <span className="font-medium text-sm min-w-[80px]">{day}</span>
                                            </div>

                                            {!hours.is_closed ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        type="time"
                                                        className="w-24 h-8 px-2"
                                                        value={hours.open_time || ""}
                                                        onChange={(e) => {
                                                            const newHours = [...structuredOpeningHours];
                                                            const existingIndex = newHours.findIndex(h => h.weekday === index);
                                                            const updated = { ...hours, open_time: e.target.value };
                                                            if (existingIndex >= 0) newHours[existingIndex] = updated;
                                                            else newHours.push(updated);
                                                            setStructuredOpeningHours(newHours);
                                                        }}
                                                    />
                                                    <span className="text-muted-foreground">-</span>
                                                    <Input
                                                        type="time"
                                                        className="w-24 h-8 px-2"
                                                        value={hours.close_time || ""}
                                                        onChange={(e) => {
                                                            const newHours = [...structuredOpeningHours];
                                                            const existingIndex = newHours.findIndex(h => h.weekday === index);
                                                            const updated = { ...hours, close_time: e.target.value };
                                                            if (existingIndex >= 0) newHours[existingIndex] = updated;
                                                            else newHours.push(updated);
                                                            setStructuredOpeningHours(newHours);
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-xs text-muted-foreground italic mr-4">{t("common.closed")}</span>
                                            )}
                                        </div>
                                    );
                                })}

                                {isManual && (
                                    <div className="mt-4">
                                        <Label htmlFor="opening-hours-text">{t("addShop.openingHoursNotesLabel")}</Label>
                                        <Textarea
                                            id="opening-hours-text"
                                            placeholder={t("addShop.openingHoursNotesPlaceholder")}
                                            value={openingHours}
                                            onChange={(e) => setOpeningHours(e.target.value)}
                                            className="mt-1"
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 3: Features & Categories */}
                    {(isManual || name) && (
                        <Card className="shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-lg">{t("addShop.featuresTitle")}</CardTitle>
                                <CardDescription>{t("addShop.featuresDesc")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="charity"
                                            checked={hasCharity}
                                            onCheckedChange={(v) => setHasCharity(!!v)}
                                        />
                                        <Label htmlFor="charity" className="text-sm font-medium leading-none cursor-pointer">{t("addShop.charity")}</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="horeca"
                                            checked={hasHoreca}
                                            onCheckedChange={(v) => setHasHoreca(!!v)}
                                        />
                                        <Label htmlFor="horeca" className="text-sm font-medium leading-none cursor-pointer">{t("addShop.horeca")}</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="card"
                                            checked={hasCardPayment}
                                            onCheckedChange={(v) => setHasCardPayment(!!v)}
                                        />
                                        <Label htmlFor="card" className="text-sm font-medium leading-none cursor-pointer">{t("addShop.cardPayment")}</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="cash"
                                            checked={hasCashPayment}
                                            onCheckedChange={(v) => setHasCashPayment(!!v)}
                                        />
                                        <Label htmlFor="cash" className="text-sm font-medium leading-none cursor-pointer">{t("addShop.cashPayment")}</Label>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <Label>{t("addShop.categories")}</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {CATEGORIES.map(cat => (
                                            <Button
                                                key={cat}
                                                variant={selectedCategories.includes(cat) ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => toggleCategory(cat)}
                                                className="rounded-full flex items-center gap-2"
                                            >
                                                <CategoryIcon category={cat} size={14} />
                                                {cat}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 4: Photos */}
                    {(isManual || name) && (
                        <Card className="shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-lg text-primary flex items-center gap-2">
                                    <Camera className="h-5 w-5" />
                                    {t("addShop.photosTitle")}
                                </CardTitle>
                                <CardDescription>{t("addShop.photosDesc")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="border rounded-xl p-4 bg-slate-50 space-y-3">
                                    <Label className="font-semibold">{t("addShop.facadeTitle")}</Label>
                                    {facadeImage ? (
                                        <div className="relative w-40 h-28 rounded-xl overflow-hidden border bg-white group">
                                            <img src={facadeImage} className="w-full h-full object-cover" alt={t("addShop.facadePreviewAlt")} />
                                            <button
                                                type="button"
                                                onClick={() => setFacadeImage(null)}
                                                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-all opacity-0 group-hover:opacity-100"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <ImageCaptureInput
                                            onImage={(img) => {
                                                setPendingImages([img]);
                                                setCroppingIndex(0);
                                                setCroppingTarget("facade");
                                            }}
                                            label={t("addShop.makeFacadePhoto")}
                                            className="bg-primary hover:bg-primary/95"
                                        />
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                        {t("addShop.facadeHint")}
                                    </p>
                                </div>
                                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50 flex flex-col items-center gap-4 group">
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium text-slate-700">{t("addShop.sharePhotosTitle")}</p>
                                        <p className="text-xs text-slate-400">{t("addShop.sharePhotosDesc")}</p>
                                    </div>

                                    <ImageCaptureInput
                                        multiple
                                        onImages={(newImages) => {
                                            setPendingImages(newImages);
                                            setCroppingIndex(0);
                                            setCroppingTarget("gallery");
                                        }}
                                        label={t("scanner.start.title")}
                                        className="bg-primary hover:bg-primary/95"
                                    />
                                </div>

                                <ImageCropperModal
                                    open={croppingIndex !== -1}
                                    onOpenChange={(open) => !open && setCroppingIndex(-1)}
                                    image={pendingImages[croppingIndex] || ""}
                                    onCrop={(cropped) => {
                                        if (croppingTarget === "facade") {
                                            setFacadeImage(cropped);
                                            setCroppingIndex(-1);
                                            setPendingImages([]);
                                        } else {
                                            setImages((prev) => [...prev, cropped].slice(0, 4));
                                            if (croppingIndex < pendingImages.length - 1) {
                                                setCroppingIndex(prev => prev + 1);
                                            } else {
                                                setCroppingIndex(-1);
                                                setPendingImages([]);
                                            }
                                        }
                                    }}
                                />

                                {images.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                                        {images.map((img, idx) => (
                                            <div key={idx} className="relative aspect-square group">
                                                <img
                                                    src={img}
                                                    className="w-full h-full object-cover rounded-xl border border-slate-100 shadow-sm"
                                                    alt={t("addShop.previewAlt")}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(idx)}
                                                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-all opacity-0 group-hover:opacity-100"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Footer Action */}
                    {(isManual || name) && (
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3 items-start">
                                {googlePlaceId ? (
                                    <>
                                        <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-semibold">{t("addShop.instantAddTitle")}</p>
                                            <p>{t("addShop.instantAddDesc")}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                                        <div className="text-sm text-amber-800">
                                            <p className="font-semibold">{t("addShop.manualReviewTitle")}</p>
                                            <p>{t("addShop.manualReviewDesc")}</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <Button
                                onClick={handleSubmit}
                                disabled={loading}
                                size="lg"
                                className="w-full text-lg h-14 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
                            >
                                {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <PlusCircle className="h-5 w-5 mr-2" />}
                                {t("shops.addShop")}
                            </Button>
                        </div>
                    )}
                </div>
            </main>

            {existingShopOverlay.open && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => setExistingShopOverlay((prev) => ({ ...prev, open: false }))}
                >
                    <div
                        className="w-full max-w-md rounded-2xl border bg-white shadow-xl p-5 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold">{t("addShop.shopExistsTitle")}</h3>
                        <p className="text-sm text-muted-foreground">
                            {existingShopOverlay.shopName
                                ? t("addShop.shopExistsDesc", { name: existingShopOverlay.shopName })
                                : t("addShop.shopExistsOverview")}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                className="bg-magenta hover:bg-magenta/90 text-white"
                                onClick={() => {
                                    setExistingShopOverlay((prev) => ({ ...prev, open: false }));
                                    setLocation(existingShopOverlay.shopUrl);
                                }}
                            >
                                {t("addShop.viewShop")}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setExistingShopOverlay((prev) => ({ ...prev, open: false }))}
                            >
                                {t("shops.cancel")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
